import { useMemo, useState } from 'react';
import { endpoints } from '@/api/endpoints';
import { useApi } from '@/api/useApi';
import { LoadingState, EmptyState } from '@/components/EmptyState';
import { ErrorBanner } from '@/components/ErrorBanner';
import { StatusBadge, statusLabel } from '@/components/StatusBadge';
import { useLanguage } from '@/i18n/LanguageContext';
import { formatDateTime } from '@/i18n/format';
import type { QueueBucket, QueueRow, SlaState } from '@/api/types';
import type { TranslateFn } from '@/i18n/LanguageContext';
import type { TranslationKey } from '@/i18n/dictionaries/en';

/**
 * FBS EXECUTION epic, Part A4 — the To pick/To pack/Urgent/Problems queue.
 * This is the exact structure this project's roadmap conversations kept
 * coming back to as the real differentiator vs. competitors' channel-first
 * navigation: a warehouse operator thinks "what do I pick next / what's on
 * fire", never "show me my WB orders". Channel is a plain badge on each row
 * here — never a tab, never a filter that changes which SECTION a row
 * belongs to.
 *
 * i18n (EN/RU) epic: strings render through the shared t() lookup
 * (@/i18n/LanguageContext) - see that module's own comment for the overall
 * approach (flat dictionary, no page duplication, no heavy library).
 */

const TABS: { key: QueueBucket; labelKey: TranslationKey }[] = [
  { key: 'TO_PICK', labelKey: 'queue.toPick' },
  { key: 'TO_PACK', labelKey: 'queue.toPack' },
  { key: 'URGENT', labelKey: 'queue.urgent' },
  { key: 'PROBLEMS', labelKey: 'queue.problems' },
];

/**
 * The queue's status column is a CALCULATED deadline status (packages/core/src/model/sla.ts computePriorityState:
 * deadline vs now, blockers, thresholds), not a priority anyone assigns - so it is labelled as a deadline status and
 * explained here, never given an editor. The two windows mirror DEFAULT_URGENT_WINDOW_MINUTES (180) and
 * DEFAULT_APPROACHING_WINDOW_MINUTES (360) in that file.
 */
const SLA_URGENT_HOURS = 3;
const SLA_APPROACHING_HOURS = 6;
const SLA_STATES: readonly SlaState[] = ['normal', 'approaching_cutoff', 'urgent', 'blocked', 'overdue'];

export function SlaHelp(): JSX.Element {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  return (
    <div className="sla-help">
      <button
        type="button"
        className="help-toggle"
        aria-expanded={open}
        aria-controls="sla-help-panel"
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setOpen(false);
        }}
      >
        <span aria-hidden="true">?</span> {t('queue.slaHelpButton')}
      </button>
      {open && (
        <section id="sla-help-panel" className="card help-panel" aria-labelledby="sla-help-title">
          <h2 id="sla-help-title" className="card-title">{t('queue.slaHelpTitle')}</h2>
          <p>{t('queue.slaHelpIntro')}</p>
          <p className="field-hint">{t('queue.slaHelpBasis')}</p>
          <dl className="sla-help-list">
            {SLA_STATES.map((s) => (
              <div key={s} className="sla-help-row">
                <dt><StatusBadge value={s} /></dt>
                <dd>{t(`queue.slaHelp.${s}` as never, { urgent: String(SLA_URGENT_HOURS), approaching: String(SLA_APPROACHING_HOURS) })}</dd>
              </div>
            ))}
          </dl>
          <div className="form-actions">
            <button type="button" onClick={() => setOpen(false)}>{t('common.close')}</button>
          </div>
        </section>
      )}
    </div>
  );
}

function formatDeadline(row: QueueRow, t: TranslateFn, locale: 'en' | 'ru'): string {
  const at = row.sla.deadline.effectiveAt;
  if (!at) return '—';
  const label = formatDateTime(at, locale);
  return row.sla.deadline.source === 'DERIVED' ? t('queue.deadlineEstimated', { deadline: label }) : label;
}

function blockerSummary(row: QueueRow, t: TranslateFn): string {
  if (row.sla.blockers.length === 0) return '';
  return row.sla.blockers.map((b) => statusLabel(t, b.type)).join(', ');
}

export function QueuePage(): JSX.Element {
  const [tab, setTab] = useState<QueueBucket>('TO_PICK');
  const result = useApi(() => endpoints.queue(), []);
  const { t, locale } = useLanguage();

  const counts = useMemo(() => {
    if (result.status !== 'success') return { TO_PICK: 0, TO_PACK: 0, URGENT: 0, PROBLEMS: 0 };
    const c = { TO_PICK: 0, TO_PACK: 0, URGENT: 0, PROBLEMS: 0 };
    for (const row of result.data.rows) {
      for (const b of row.buckets) c[b] += 1;
    }
    return c;
  }, [result]);

  const rowsForTab = result.status === 'success' ? result.data.rows.filter((r) => r.buckets.includes(tab)) : [];

  return (
    <div>
      <div className="page-header">
        <h1>{t('queue.title')}</h1>
      </div>
      <SlaHelp />
      <div className="filters" role="tablist" aria-label={t('queue.sectionsAriaLabel')}>
        {TABS.map((tabDef) => (
          <button
            key={tabDef.key}
            type="button"
            role="tab"
            aria-selected={tab === tabDef.key}
            className={tab === tabDef.key ? 'active' : ''}
            onClick={() => setTab(tabDef.key)}
          >
            {t(tabDef.labelKey)} ({counts[tabDef.key]})
          </button>
        ))}
      </div>

      {result.status === 'loading' && <LoadingState />}
      {result.status === 'error' && <ErrorBanner error={result.error} onRetry={result.reload} />}
      {result.status === 'success' &&
        (rowsForTab.length === 0 ? (
          <EmptyState title={t('queue.emptyTitle')} hint={t('queue.emptyHint')} />
        ) : (
          <div className="table-scroll"><table>
            <thead>
              <tr>
                <th>{t('queue.colOrder')}</th>
                <th>{t('queue.colState')}</th>
                <th>{t('queue.colChannel')}</th>
                <th>{t('queue.colPriority')}</th>
                <th>{t('queue.colDeadline')}</th>
                <th>{t('queue.colBlockers')}</th>
              </tr>
            </thead>
            <tbody>
              {rowsForTab.map((row) => (
                <tr key={row.salesOrderId} onClick={() => (window.location.href = `/shipments/${row.salesOrderId}`)}>
                  <td>{row.number}</td>
                  <td>
                    <StatusBadge value={row.state} />
                  </td>
                  <td>{row.channelKind ?? t('queue.channelManual')}</td>
                  <td>
                    <StatusBadge value={row.sla.state} />
                  </td>
                  <td>{formatDeadline(row, t, locale)}</td>
                  <td>{blockerSummary(row, t)}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
        ))}
    </div>
  );
}
